import View from '@/Views/View';
import Loader from '@/Utility/Loader';
import Factory from '@/Factories/Factory';
import GenericObject from '@/Model/GenericObject';
import ComponentManager from '@/Management/ComponentManager';
import ListViewViewModel from '@/ViewModels/Views/ListViewViewModel';
import ListViewViewFactory from '@/Factories/ViewFactories/ListViewViewFactory';
import AutoGeneratingSummaryViewFactory from '@/Factories/SummaryViewFactories/AutoGeneratingSummaryViewFactory';
import SplitterPaneViewModel from '@/ViewModels/Controls/SplitterPaneViewModel';
import { IListViewDTO } from "coreframetypings/dto";
import {DisposeBehavior} from '#/DefaultViewExtensionInterfaces';
import Utilities from '@/Utility/Utilities';
import layout from './ListViewView.html?raw';
export default class ListViewView extends View {
    private view: JQuery<HTMLElement>;

    private summaryViewVertical: JQuery<HTMLElement>;
    private summaryViewHorizontal: JQuery<HTMLElement>;
    private factory: AutoGeneratingSummaryViewFactory;
    protected viewModel: ListViewViewModel;
    protected element: JQuery<HTMLElement>;
    protected customLayout: string;
    private isChecking: boolean;

    public constructor(viewModel: ListViewViewModel, element?: JQuery<HTMLElement>, customLayout?: string) {
        super();

        this.viewModel = viewModel;
        this.element = element;
        this.customLayout = customLayout;
        this.undisposeProperties.push('viewModel');
    }

    public getView(): JQuery<HTMLElement> {
        if (this.view != null) {
            return this.view;
        }

        this.view = this.applyBindings(this.element, this.viewModel, this.customLayout != null ? this.customLayout : layout);

        this.setupSummaryViewRerendering();

        return this.view;
    }

    private setupSummaryViewRerendering(): void {
        const debouncedRerenderSummaryView = _.debounce(() => this.rerenderSummaryViewAsync(), 180);

        this.defineSubscription(this.viewModel._refreshSummaryView, async (refresh) => {
            if (refresh)
                await debouncedRerenderSummaryView();
        });
        this.defineSubscription(this.viewModel.listView, async () => await debouncedRerenderSummaryView());
        this.defineSubscription(this.viewModel.currentSummaryViewPDO, async () => await debouncedRerenderSummaryView());
        this.defineSubscription(this.viewModel.horizontalSummaryViewIsOpen, async () => await debouncedRerenderSummaryView());
        this.defineSubscription(this.viewModel.verticalSummaryViewIsOpen, async () => await debouncedRerenderSummaryView());

        debouncedRerenderSummaryView();
    }

    private async rerenderSummaryViewAsync(): Promise<void> {
        if (this.isDisposed || this.viewModel.isDisposed || this.isChecking)
            return;

        this.isChecking = true;

        try {
            const listView = this.viewModel.listView();
            if (listView == null)
                return;

            this.summaryViewHorizontal = this.view.find('.summaryview-horizontal');
            this.summaryViewVertical = this.view.find('.summaryview-vertical');

            if (!listView.summaryViewUIFactory) {
                if (listView.summaryViewAutoGenerating === true) {
                    listView.summaryViewUIFactory = 'AutoGeneratingSummaryViewFactory';
                } else {
                    return;
                }
            }

            const gridItem = this.viewModel.currentSummaryViewPDO();
            const force = this.viewModel._refreshSummaryView();
            await this.generateSummaryViewAsync(listView, gridItem, force);
            if (this.isDisposed)
                return;

            this.viewModel._refreshSummaryView(false);
        } finally {
            this.isChecking = false;
        }
    }

    public customizeOrientation(orientation: string): string {
        return orientation;
    }

    private async generateSummaryViewAsync(listView: IListViewDTO, gridItem: string, forceRefresh: boolean): Promise<void> {
        let scrollPosition: number | undefined = undefined;

        if (this.factory != null) {
            if (this.factory.pdoId === gridItem && gridItem != null && forceRefresh !== true)
                return;

            scrollPosition = this.factory.summaryView?.getScrollPosition();
            this.factory.dispose();
        }

        const currentFactory = this.createSummaryViewFactory(listView, gridItem);

        // Speicherung der Factory in einer lokalen Variablen ermöglicht es weiter unten beim Abrufen des GenericObject
        // eine korrekte disposed-Prüfung durchzuführen.
        // Die zusätzliche Speicherung der Factory als Property erlaubt ein sauberes Disposing, wenn ListViewView disposed wird.
        this.factory = currentFactory;

        const orientation = this.customizeOrientation(currentFactory.orientation);
        this.setBusy(orientation, true);

        if (currentFactory.showAlways || gridItem)
            this.showPane(orientation, 100 - currentFactory.screenRatio);
        else
            this.hidePane(orientation);

        const onCurrentFactoryPrepared = async () => {
            if (this.isDisposed || currentFactory.isDisposed)
                return;

            const tuple = await currentFactory.getTupleAsync();
            if (this.isDisposed || this.viewModel.isDisposed || !tuple?.view)
                return;

            this.setSummaryView(orientation, tuple.view);
            this.setBusy(orientation, false);

            if (scrollPosition !== undefined)
                currentFactory.summaryView?.scrollTo(scrollPosition);
        };

        if (!gridItem || !this.shouldShowSummaryView(orientation)) {
            this.setSummaryView(orientation, $());
            this.setBusy(orientation, false);
            return;
        }

        // Damit das Aktualisieren einer SV klappt, dürfen wir nicht pdoId nutzen, um zu prüfen, ob eine Anzeige nötig ist
        currentFactory.pdoId = gridItem;

        if (listView.summaryViewAutoGenerating !== false) {
            await onCurrentFactoryPrepared();
            return;
        }

        const properties = currentFactory.getNeededProperties();

        this.setBusy(orientation, true);
        const genericObject: GenericObject = await this.evaluate(this.viewModel.dataGrid.getGenericObject(gridItem, properties));
        if (this.isDisposed)
            return;

        this.setBusy(orientation, false);
        if (!currentFactory.isDisposed) {
            currentFactory.genericObject = genericObject;
            await onCurrentFactoryPrepared();
        }
    }

    protected createSummaryViewFactory(listView: IListViewDTO, gridItem: string): AutoGeneratingSummaryViewFactory {
        return Factory.createInstance<AutoGeneratingSummaryViewFactory>(listView.summaryViewUIFactory + '?' + listView.summaryViewParameters, {
            locator: 'sv',
            viewModel: this.viewModel,
            pdTypeName: listView.typeName,
            ergName: listView.ergName
        }, 'AutoGeneratingSummaryViewFactory');
    }

    protected shouldShowSummaryView(orientation: string): boolean {
        switch (orientation) {
            case "Horizontal":
                return !this.viewModel.horizontalSummaryViewClosed;
            case "Vertical":
                return !this.viewModel.verticalSummaryViewClosed;
            default:
                throw new Error(`Unsupported orientation: ${orientation}`);
        }
    }

    private setSummaryView(orientation: string, summaryView: JQuery<HTMLElement>) {
        switch (orientation) {
            case 'Horizontal':
                this.summaryViewHorizontal.empty();
                this.summaryViewHorizontal.prepend(summaryView);
                this.viewModel.splitterHorizontal.isRequired(false);
                this.viewModel.splitterVertical.isRequired(this.viewModel.currentSummaryViewPDO() != null);
                break;

            case 'Vertical':
                this.summaryViewVertical.empty();
                this.summaryViewVertical.prepend(summaryView);
                this.viewModel.splitterVertical.isRequired(false);
                this.viewModel.splitterHorizontal.isRequired(this.viewModel.currentSummaryViewPDO() != null);
                break;
        }
    }

    private setBusy(orientation: string, isBusy: boolean): void {
        if (!this.viewModel || this.viewModel.isDisposed)
            return;

        let pane: SplitterPaneViewModel;
        switch (orientation) {
            case 'Horizontal':
                pane = this.viewModel.splitterVertical?.panes[1];
                break;

            case 'Vertical':
                pane = this.viewModel.splitterHorizontal?.panes[1];
                break;
        }

        if (!pane || pane.isDisposed)
            return;

        pane.isBusy(isBusy);
    }
    
    private showPane(orientation: string, screenRatio: number): void {
        if (Utilities.isMobileView() || !this.shouldShowSummaryView(orientation))
            return;

        switch (orientation) {
            case 'Horizontal':
                const verticalPane = this.viewModel.splitterVertical.panes[1];
                if (verticalPane.isExpanded()) {
                    return;
                }

                verticalPane.isVisible(true);
                verticalPane.isExpanded(true);

                this.async(() => {
                    this.viewModel.updateSummaryViewSize(verticalPane, this.element.height(), screenRatio);
                });

                break;

            case 'Vertical':
                const horizontalPane = this.viewModel.splitterHorizontal.panes[1];
                if (horizontalPane.isExpanded()) {
                    return;
                }

                horizontalPane.isVisible(true);
                horizontalPane.isExpanded(true);

                this.async(() => {
                    this.viewModel.updateSummaryViewSize(horizontalPane, this.element.width(), screenRatio);
                });

                break;
        }
    }

    private hidePane(orientation: string): void {
        switch (orientation) {
            case 'Horizontal':
                this.viewModel.closeHorizontalSummaryViewTemporarily();
                break;
            case 'Vertical':
                this.viewModel.closeVerticalSummaryViewTemporarily();
                break;
        }
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setView('ListViewView', ListViewView, 'RADPresentation.HTMLClient.DefaultViewExtensions');